import React from 'react'
import { cn } from '@/utils'

type BleedFrameProps = {
  width: number
  height: number
  borderPx?: number
  borderColor?: string
  dashed?: boolean
  /**
   * Paint `--doc-bleed-bg` across the full box. Set false when bleeds are hidden so the margin
   * outside the trim rect (`.doc-page-fill` covers the trim rect itself) goes transparent instead
   * of showing a bleed-sized background past the dashed trim line.
   */
  paintBackground?: boolean
  /**
   * Excludes a region of this frame (background and children alike) regardless of
   * `paintBackground`. `SinglePageDocument` uses this to keep its own background from ever
   * covering the virtual spine-side margin — a "there's no facing page here" gap, not a real
   * bleed edge — which otherwise reads as an unwanted white (or themed) strip past the dashed trim
   * line whenever bleeds are shown.
   */
  clipPath?: string
  children?: React.ReactNode
  className?: string
}

const BleedFrame: React.FC<BleedFrameProps> = ({
  width,
  height,
  borderPx = 0,
  borderColor = 'var(--doc-trim-border)',
  dashed = false,
  paintBackground = true,
  clipPath,
  children,
  className,
}) => {
  return (
    <div
      className={cn(
        'relative box-border',
        paintBackground && 'bg-[color:var(--doc-bleed-bg)]',
        dashed ? 'border-dashed' : 'border-solid',
        className
      )}
      style={{
        width,
        height,
        borderWidth: borderPx,
        borderColor,
        ...(clipPath ? { clipPath } : {}),
      }}
    >
      {children}
    </div>
  )
}

export default BleedFrame
